import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Modal, ModalClose, Button, Icon } from '../../campaign-os';
import { EmailPreview } from '../EmailPreview';
import { useToast } from '../../../../components/ui/Toast';
import { useAuth } from '../../../../contexts/AuthContext';
import { templateService, getErrorMessage, getErrorDetails } from '../../../../services/apiService';
import { useScratchEditor, ScratchRibbon, ScratchSheet, SelectionBubble } from '../editor/ScratchPad';
import {
  TEMPLATE_MODES,
  NEW_TEMPLATE_MODE,
  SUBJECT_SOFT_MAX,
  SHORTCUTS,
  modeOf,
  templateBodyFor,
  isScratchEmpty,
  countsOf,
  draftStorageKey,
  STARTER_SCRATCH_HTML,
} from '../data/templateModes';

/**
 * Template editor — creates and edits REAL rows in EmailTemplate, as a document workspace
 * (user decision, 25 Oct 2026: "like Google Docs or Word, not a form"):
 *
 *   header   — the template name (inline), save state, the Scratch pad / Guided form slider,
 *              Preview, Send me a test, Save.
 *   ribbon   — the scratch pad's toolbar (or, for the guided form, a short note), Details.
 *   canvas   — the paper in the middle (the mail: subject line, fixed Amber header, the body,
 *              fixed footer; zoomable) and the live preview beside it (desktop / phone).
 *   status   — saved state, counts, zoom.
 *
 * The preview is rendered by the server through the SAME path a campaign sends with
 * (POST /email-templates/preview → renderTemplate), so "what I see is what goes out" is a fact.
 * Unsaved work is guarded (Ctrl+S saves; leaving asks; a browser draft is kept for recovery).
 *
 * `useTemplateDraft` owns the state; `TemplateDocument` is the workspace; `TemplateEditor` is
 * the full-screen overlay around it (New Template, quick edit), and TemplateEditorPage.jsx is the
 * route around the same workspace.
 */

/** Flatten the API's field-level validation details into one readable line. */
export function describeError(err, fallback) {
  const details = getErrorDetails(err);
  if (details && typeof details === 'object') {
    const parts = Object.entries(details).map(
      ([field, msgs]) => `${field.replace(/^(body|query|params)\./, '')}: ${[].concat(msgs).join(', ')}`
    );
    if (parts.length) return parts.join(' · ');
  }
  return getErrorMessage(err) || fallback;
}

const CATEGORIES = ['Compliance', 'Verification', 'Outreach', 'Custom'];

const FIELD =
  'w-full rounded-xl border border-cos-line bg-white px-3 py-2.5 text-sm text-cos-ink placeholder:text-cos-ink-secondary/60 focus:border-cos-teal/50 focus:outline-none focus:ring-2 focus:ring-cos-teal/15 transition';
const LABEL = 'mb-1.5 block text-[13px] font-semibold text-cos-ink';

function Field({ label, hint, children }) {
  return (
    <div>
      <label className={LABEL}>{label}</label>
      {children}
      {hint ? <p className="mt-1 text-[12px] leading-[17px] text-cos-ink-secondary">{hint}</p> : null}
    </div>
  );
}

const toText = (arr) => (Array.isArray(arr) ? arr.join('\n\n') : '');
const toLinesText = (arr) => (Array.isArray(arr) ? arr.join('\n') : '');

const fmtTime = (d) => (d ? new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');

const readDraft = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
const writeDraft = (key, value) => {
  try {
    if (value) window.localStorage.setItem(key, JSON.stringify(value));
    else window.localStorage.removeItem(key);
  } catch {
    /* storage unavailable — the server copy is the only one, as before */
  }
};

// ----------------------------------------------------------------------------- draft state

/** Everything the workspace needs: the draft, its mode, the server-rendered preview, save. */
export function useTemplateDraft(template, { initialMode, initialDraft } = {}) {
  const toast = useToast();
  const [saved, setSaved] = useState(template ?? null); // the row as last saved (null = new)
  const isNew = !saved;
  const [mode, setMode] = useState(initialMode ?? (template ? modeOf(template) : NEW_TEMPLATE_MODE));
  const [d, setD] = useState(() => ({
    name: template?.name ?? '',
    description: template?.description ?? '',
    category: template?.category ?? 'Outreach',
    subject: template?.subject ?? '',
    previewText: template?.preview_text ?? '',
    bodyHtml: template?.body_html || STARTER_SCRATCH_HTML,
    eyebrow: template?.eyebrow ?? '',
    heading: template?.heading ?? '',
    lead: template?.lead ?? '',
    paragraphs: toText(template?.paragraphs),
    calloutTitle: template?.callout_title ?? '',
    calloutLines: toLinesText(template?.callout_lines),
    ctaLabel: template?.cta_label ?? '',
    ctaUrl: template?.cta_url ?? '',
    signoffName: template?.signoff_name ?? '',
    partnerLogo: template?.show_partner_logo === true,
    sharedAudience: template?.shared_audience === true,
    greeting: template?.greeting ?? '',
    ...(initialDraft ?? {}),
  }));
  const set = useCallback((key) => (value) => setD((prev) => ({ ...prev, [key]: value })), []);

  const ctaHalf = mode === 'blocks' && Boolean(d.ctaLabel.trim()) !== Boolean(d.ctaUrl.trim());
  const body = useMemo(() => templateBodyFor(mode, d), [mode, d]);

  // Dirty = different from what was last loaded or saved (a draft handed over counts as dirty).
  const signature = JSON.stringify(body);
  const baseline = useRef(initialDraft ? null : signature);
  const dirty = baseline.current !== signature;
  const [savedAt, setSavedAt] = useState(template?.updated_at ?? null);

  // Browser-side recovery: the latest unsaved state, cleared on save.
  const storageKey = draftStorageKey(saved?.id);
  const [recoverable, setRecoverable] = useState(() => {
    const stored = readDraft(draftStorageKey(template?.id));
    if (!stored || initialDraft) return null;
    const newer = !template?.updated_at || new Date(stored.at) > new Date(template.updated_at);
    return newer && JSON.stringify(templateBodyFor(stored.mode, stored.d)) !== signature ? stored : null;
  });
  useEffect(() => {
    if (!dirty) return undefined;
    const t = setTimeout(() => writeDraft(storageKey, { mode, d, at: new Date().toISOString() }), 700);
    return () => clearTimeout(t);
  }, [dirty, mode, d, storageKey]);
  const restoreDraft = () => {
    if (!recoverable) return;
    setMode(recoverable.mode);
    setD(recoverable.d);
    setRecoverable(null);
  };
  const discardDraft = () => {
    writeDraft(storageKey, null);
    setRecoverable(null);
  };

  const [preview, setPreview] = useState({ html: '', subject: '', sample: null, error: null, rendering: false });
  // Render through the real send-time renderer, debounced so typing stays smooth.
  useEffect(() => {
    if (ctaHalf) return undefined;
    let cancelled = false;
    const t = setTimeout(async () => {
      setPreview((p) => ({ ...p, rendering: true }));
      try {
        const res = await templateService.preview(body);
        if (!cancelled && res.success) {
          setPreview({ html: res.data.html, subject: res.data.subject, sample: res.data.sample ?? null, error: null, rendering: false });
        }
      } catch (err) {
        if (!cancelled) setPreview((p) => ({ ...p, error: describeError(err, 'Preview unavailable'), rendering: false }));
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [body, ctaHalf]);

  const [saving, setSaving] = useState(false);
  /** Saves; resolves to the saved row, or null. Warnings are toasts, never exceptions. */
  const save = async () => {
    if (d.name.trim().length < 2) {
      toast.warning('Give the template a name (top left)');
      return null;
    }
    if (d.subject.trim().length < 3) {
      toast.warning('A subject line is required — vendors see it in their inbox');
      return null;
    }
    if (mode === 'scratch' && isScratchEmpty(d.bodyHtml)) {
      toast.warning('Write the mail in the scratch pad first');
      return null;
    }
    if (ctaHalf) {
      toast.warning('A button needs both a label and a link');
      return null;
    }
    setSaving(true);
    try {
      const res = isNew ? await templateService.create(body) : await templateService.update(saved.id, body);
      if (!res.success) return null;
      baseline.current = signature;
      setSaved(res.data);
      setSavedAt(res.data?.updated_at ?? new Date().toISOString());
      writeDraft(storageKey, null);
      if (isNew) writeDraft(draftStorageKey(null), null);
      return res.data;
    } catch (err) {
      toast.error(describeError(err, 'Could not save the template'));
      return null;
    } finally {
      setSaving(false);
    }
  };

  return { isNew, saved, mode, setMode, d, set, body, ctaHalf, preview, saving, save, dirty, savedAt, recoverable, restoreDraft, discardDraft };
}

// ----------------------------------------------------------------------------- small parts

/** The slider between the two ways of writing a template. */
export function ModeSwitch({ value, onChange, className = '' }) {
  const index = TEMPLATE_MODES.findIndex((m) => m.key === value);
  return (
    <div role="radiogroup" aria-label="How this template is written" className={`relative inline-grid shrink-0 grid-cols-2 rounded-full border border-cos-line bg-white/70 p-1 ${className}`}>
      <span
        aria-hidden
        className="absolute bottom-1 top-1 w-[calc(50%-4px)] rounded-full bg-cos-teal-deep shadow-cos-glass-sm transition-transform duration-200 ease-out"
        style={{ left: 4, transform: `translateX(${Math.max(index, 0) * 100}%)` }}
      />
      {TEMPLATE_MODES.map((m) => (
        <button
          key={m.key}
          type="button"
          role="radio"
          aria-checked={value === m.key}
          title={m.hint}
          onClick={() => onChange(m.key)}
          className={[
            'relative z-10 inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-[12.5px] font-semibold transition-colors',
            value === m.key ? 'text-white' : 'text-cos-ink-secondary hover:text-cos-ink',
          ].join(' ')}
        >
          <Icon name={m.icon} className="text-[17px]" />
          {m.label}
        </button>
      ))}
    </div>
  );
}

/** A small anchored card; closes on an outside click or Escape. */
function Anchored({ open, onClose, children, className = '' }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !ref.current.parentElement?.contains(e.target)) onClose();
    };
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div ref={ref} className={`sp-popover sp-popover-right ${className}`}>
      {children}
    </div>
  );
}

/** Name and subject, on the sheet — the two lines every mail has above its body. */
function Envelope({ d, set }) {
  const len = d.subject.length;
  return (
    <div className="sp-envelope">
      <div className="sp-env-row">
        <span className="sp-env-label">Template</span>
        <input className="sp-env-input" value={d.name} onChange={(e) => set('name')(e.target.value)} placeholder="Untitled template — the name your team sees" maxLength={120} />
      </div>
      <div className="sp-env-row">
        <span className="sp-env-label">Subject</span>
        <input className="sp-env-input sp-env-subject" value={d.subject} onChange={(e) => set('subject')(e.target.value)} placeholder="What the vendor sees in the inbox" maxLength={200} />
        <span className={`sp-env-counter ${len > SUBJECT_SOFT_MAX ? 'is-over' : ''}`} title={len > SUBJECT_SOFT_MAX ? `Inboxes cut a subject around ${SUBJECT_SOFT_MAX} characters` : 'Subject length'}>
          {len}/{SUBJECT_SOFT_MAX}
        </span>
      </div>
    </div>
  );
}

/** Category, internal description, preview line — in a popover, out of the way. */
function DetailsPanel({ d, set, mode }) {
  return (
    <div className="flex w-[360px] flex-col gap-3">
      <Field label="Category">
        <select className={FIELD} value={d.category} onChange={(e) => set('category')(e.target.value)}>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Internal description" hint="Only your team sees this, on the Templates screen.">
        <input className={FIELD} value={d.description} onChange={(e) => set('description')(e.target.value)} placeholder="What this template is for" />
      </Field>
      <Field label="Preview text" hint={mode === 'scratch' ? 'The grey line inboxes show next to the subject. Blank uses the first words of the mail.' : 'The grey line inboxes show next to the subject.'}>
        <input className={FIELD} value={d.previewText} onChange={(e) => set('previewText')(e.target.value)} placeholder="A short summary of the email" maxLength={200} />
      </Field>
    </div>
  );
}

/** The guided form: exactly the fields the broadcast renderer consumes. */
function BlocksForm({ d, set, ctaHalf }) {
  return (
    <div className="flex flex-col gap-4">
      <Field label="Sign-off team" hint="Named above the company at the end. Blank uses “Vendor Management Team”.">
        <input className={FIELD} value={d.signoffName} onChange={(e) => set('signoffName')(e.target.value)} placeholder="Vendor Management Team" maxLength={80} />
      </Field>
      <Field label="Genessence partner strip" hint="Their mark above the footer — only for mails that actually involve Genessence.">
        <label className="flex cursor-pointer items-center gap-2 text-[13px] text-cos-ink">
          <input type="checkbox" checked={d.partnerLogo} onChange={(e) => set('partnerLogo')(e.target.checked)} className="h-4 w-4 accent-cos-teal-deep" />
          Show on this template
        </label>
      </Field>
      <Field label="One shared copy (CC / BCC)" hint="For a mail sent once to the whole audience rather than one per vendor.">
        <label className="flex cursor-pointer items-center gap-2 text-[13px] text-cos-ink">
          <input type="checkbox" checked={d.sharedAudience} onChange={(e) => set('sharedAudience')(e.target.checked)} className="h-4 w-4 accent-cos-teal-deep" />
          One copy for everyone — no per-vendor details, and the greeting below applies to all
        </label>
      </Field>
      <Field
        label="Greeting"
        hint={
          d.sharedAudience
            ? 'The first line of the mail. Blank sends “Dear Partners,”. One copy reaches everyone, so do not put a vendor’s name here.'
            : 'The first line of the mail. Blank sends each vendor’s name. You can write {{params.company_name}} and {{params.vendor_code}} and they are filled in per vendor.'
        }
      >
        <input className={FIELD} value={d.greeting} onChange={(e) => set('greeting')(e.target.value)} placeholder={d.sharedAudience ? 'Dear Partners,' : 'Dear {{params.company_name}},'} />
      </Field>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[150px_minmax(0,1fr)]">
        <Field label="Eyebrow">
          <input className={FIELD} value={d.eyebrow} onChange={(e) => set('eyebrow')(e.target.value)} placeholder="Action required" />
        </Field>
        <Field label="Headline">
          <input className={FIELD} value={d.heading} onChange={(e) => set('heading')(e.target.value)} placeholder="Confirm the bank account we pay into" />
        </Field>
      </div>
      <Field label="Opening line">
        <textarea className={FIELD} rows={2} value={d.lead} onChange={(e) => set('lead')(e.target.value)} placeholder="Sits under the headline, slightly larger." />
      </Field>
      <Field label="Body" hint="Separate paragraphs with a blank line. Basic HTML such as <strong> is allowed.">
        <textarea className={FIELD} rows={7} value={d.paragraphs} onChange={(e) => set('paragraphs')(e.target.value)} placeholder={'First paragraph.\n\nSecond paragraph.'} />
      </Field>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Highlight box title">
          <input className={FIELD} value={d.calloutTitle} onChange={(e) => set('calloutTitle')(e.target.value)} placeholder="What we ask you to do" />
        </Field>
        <Field label="Highlight box points" hint="One per line.">
          <textarea className={FIELD} rows={3} value={d.calloutLines} onChange={(e) => set('calloutLines')(e.target.value)} placeholder={'First point.\nSecond point.'} />
        </Field>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
        <Field label="Button label">
          <input className={FIELD} value={d.ctaLabel} onChange={(e) => set('ctaLabel')(e.target.value)} placeholder="Optional" />
        </Field>
        <Field label="Button link">
          <input className={FIELD} value={d.ctaUrl} onChange={(e) => set('ctaUrl')(e.target.value)} placeholder="https://…" />
        </Field>
      </div>
      {ctaHalf ? <p className="-mt-1 text-[12px] font-medium text-cos-status-coral">A button needs both a label and a link.</p> : null}
    </div>
  );
}

const PREVIEW_ZOOMS = [
  [null, 'Fit'],
  [0.75, '75%'],
  [1, '100%'],
  [1.25, '125%'],
];

function PreviewPane({ preview, shared }) {
  const [device, setDevice] = useState('desktop');
  const [zoom, setZoom] = useState(null);
  return (
    <aside className="tpl-preview" aria-label="Live preview">
      <div className="tpl-preview-head">
        <Icon name="mark_email_read" className="text-[18px] text-cos-teal-deep" />
        <span className="text-[13px] font-semibold text-cos-ink">Preview</span>
        {preview.rendering ? <span className="text-[11.5px] text-cos-ink-secondary">rendering…</span> : null}
        <span className="sp-toolbar-grow" />
        <div className="sp-seg">
          <button type="button" title="As on a computer" aria-pressed={device === 'desktop'} className={`sp-seg-btn ${device === 'desktop' ? 'is-active' : ''}`} onClick={() => setDevice('desktop')}>
            <Icon name="computer" className="text-[17px]" />
          </button>
          <button type="button" title="As on a phone" aria-pressed={device === 'mobile'} className={`sp-seg-btn ${device === 'mobile' ? 'is-active' : ''}`} onClick={() => setDevice('mobile')}>
            <Icon name="smartphone" className="text-[17px]" />
          </button>
        </div>
        <div className="sp-seg">
          {PREVIEW_ZOOMS.map(([z, label]) => (
            <button key={label} type="button" aria-pressed={zoom === z} className={`sp-seg-btn !w-auto px-2 text-[11.5px] font-semibold ${zoom === z ? 'is-active' : ''}`} onClick={() => setZoom(z)}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <p className="tpl-preview-note pt-2">
        {shared ? 'The one copy everyone receives.' : preview.sample?.is_sample_data ? 'Shown with sample vendor details — each vendor sees their own.' : 'Exactly what the vendor receives.'}
      </p>
      {preview.subject ? (
        <p className="tpl-preview-subject">
          <span className="font-semibold text-cos-ink-secondary">Subject: </span>
          {preview.subject}
        </p>
      ) : null}
      {preview.error ? (
        <p className="mx-3 mt-2 flex items-start gap-1.5 rounded-lg border border-cos-status-amber/30 bg-cos-status-amber-bg px-3 py-2 text-[12px] leading-[17px] text-cos-status-amber">
          <Icon name="warning" className="mt-px text-[15px]" />
          <span>{preview.error}</span>
        </p>
      ) : null}
      <div className="tpl-preview-scroll">
        <EmailPreview html={preview.html} title="Template preview" zoom={zoom} device={device} />
      </div>
    </aside>
  );
}

const SHEET_ZOOMS = [0.75, 0.9, 1, 1.1, 1.25, 1.5];

// ----------------------------------------------------------------------------- the workspace

/**
 * The document workspace. `chrome` is "overlay" (the full-screen dialog: its own close button)
 * or "page" (the route: a back button). `onClose` is asked to leave; `onSaved(row)` after a save.
 */
export function TemplateDocument({ draft, chrome = 'page', onClose, onSaved, fullscreen = false, onToggleFullscreen = null }) {
  const toast = useToast();
  const { user } = useAuth() ?? {};
  const { isNew, mode, setMode, d, set, ctaHalf, preview, saving, dirty, savedAt, recoverable, restoreDraft, discardDraft } = draft;
  const [previewOpen, setPreviewOpen] = useState(() => (typeof window === 'undefined' ? true : window.innerWidth >= 1280));
  const [zoomIdx, setZoomIdx] = useState(2);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [leaveAsk, setLeaveAsk] = useState(false);
  const [testAsk, setTestAsk] = useState(false);
  const [testing, setTesting] = useState(false);
  const scrollRef = useRef(null);

  const uploadImage = useCallback(async (file) => {
    const res = await templateService.uploadImage(file);
    if (!res?.success || !res.data?.url) throw new Error('The image could not be uploaded');
    return res.data;
  }, []);
  const onImageError = useCallback((err) => toast.error(describeError(err, 'Could not add the image')), [toast]);
  const pad = useScratchEditor({ value: d.bodyHtml, onChange: set('bodyHtml'), onUploadImage: uploadImage, onError: onImageError });

  const doSave = useCallback(async () => {
    const row = await draft.save();
    if (row) {
      toast.success(isNew ? `“${row.name}” created` : 'Saved');
      onSaved?.(row);
    }
    return row;
  }, [draft, isNew, onSaved, toast]);
  const saveRef = useRef(doSave);
  saveRef.current = doSave;

  // Ctrl+S saves; leaving with unsaved work asks first (and the browser asks on close/reload).
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        saveRef.current();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => {
    if (!dirty) return undefined;
    const onUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [dirty]);
  const requestClose = () => (dirty ? setLeaveAsk(true) : onClose?.());

  const sendTest = async () => {
    setTesting(true);
    try {
      const res = await templateService.testSend(draft.body);
      if (res.success) toast.success(res.message || `Test sent to ${res.data?.to}`);
      setTestAsk(false);
    } catch (err) {
      toast.error(describeError(err, 'The test could not be sent'));
    } finally {
      setTesting(false);
    }
  };

  const counts = useMemo(() => countsOf(d.bodyHtml), [d.bodyHtml]);
  const zoom = SHEET_ZOOMS[zoomIdx];
  const status = saving ? ['is-saving', 'progress_activity', 'Saving…'] : dirty ? ['is-dirty', 'edit', 'Unsaved changes'] : ['is-saved', 'check_circle', isNew ? 'Not saved yet' : `Saved ${fmtTime(savedAt)}`];

  const rightSlot = (
    <>
      <div className="relative">
        <button type="button" className={`sp-tb-label ${detailsOpen ? 'is-active' : ''}`} onClick={() => setDetailsOpen((o) => !o)} title="Category, internal description, preview text">
          <Icon name="tune" className="text-[18px]" />
          Details
        </button>
        <Anchored open={detailsOpen} onClose={() => setDetailsOpen(false)}>
          <DetailsPanel d={d} set={set} mode={mode} />
        </Anchored>
      </div>
      <div className="relative">
        <button type="button" className={`sp-tb ${helpOpen ? 'is-active' : ''}`} title="Keyboard shortcuts" aria-label="Keyboard shortcuts" onClick={() => setHelpOpen((o) => !o)}>
          <Icon name="keyboard" className="text-[19px]" />
        </button>
        <Anchored open={helpOpen} onClose={() => setHelpOpen(false)}>
          <p className="sp-pop-title">Keyboard shortcuts</p>
          <div className="tpl-shortcuts">
            {SHORTCUTS.map(([keys, what]) => (
              <React.Fragment key={keys}>
                <kbd>{keys}</kbd>
                <span className="text-cos-ink">{what}</span>
              </React.Fragment>
            ))}
          </div>
        </Anchored>
      </div>
    </>
  );

  return (
    <div className={`tpl-doc ${chrome === 'overlay' ? 'tpl-doc-overlay' : ''}`}>
      {/* ------------------------------------------------------------ header */}
      <div className="tpl-head">
        <button type="button" className="tpl-icon-btn" title={chrome === 'overlay' ? 'Close' : 'Back to Templates'} aria-label={chrome === 'overlay' ? 'Close' : 'Back to Templates'} onClick={requestClose}>
          <Icon name={chrome === 'overlay' ? 'close' : 'arrow_back'} className="text-[21px]" />
        </button>
        <div className="tpl-head-title">
          <input className="tpl-head-name" value={d.name} onChange={(e) => set('name')(e.target.value)} placeholder={isNew ? 'Untitled template' : 'Template name'} aria-label="Template name" maxLength={120} />
          <div className="tpl-head-meta">
            <span className={`tpl-status-chip ${status[0]}`}>
              <Icon name={status[1]} className={`text-[13px] ${saving ? 'animate-spin' : ''}`} />
              {status[2]}
            </span>
            <span>{d.category}</span>
          </div>
        </div>
        <div className="tpl-head-actions">
          <ModeSwitch value={mode} onChange={setMode} />
          <button type="button" className={`tpl-icon-btn ${previewOpen ? 'is-active' : ''}`} title={previewOpen ? 'Hide the preview' : 'Show the preview'} aria-label={previewOpen ? 'Hide the preview' : 'Show the preview'} aria-pressed={previewOpen} onClick={() => setPreviewOpen((o) => !o)}>
            <Icon name="visibility" className="text-[21px]" />
          </button>
          {onToggleFullscreen ? (
            <button type="button" className={`tpl-icon-btn ${fullscreen ? 'is-active' : ''}`} title={fullscreen ? 'Exit full screen' : 'Full screen'} aria-label={fullscreen ? 'Exit full screen' : 'Full screen'} aria-pressed={fullscreen} onClick={onToggleFullscreen}>
              <Icon name={fullscreen ? 'fullscreen_exit' : 'fullscreen'} className="text-[21px]" />
            </button>
          ) : null}
          <Button variant="secondary" icon="send" onClick={() => setTestAsk(true)} disabled={testing} title="Mail this template to your own address, filled in as a sample vendor">
            Send me a test
          </Button>
          <Button variant="primary" icon="save" onClick={doSave} disabled={saving}>
            {saving ? 'Saving…' : isNew ? 'Create template' : 'Save'}
          </Button>
        </div>
      </div>

      {/* ------------------------------------------------------------ ribbon */}
      <div className="tpl-ribbon">
        {mode === 'scratch' ? (
          <ScratchRibbon editor={pad.editor} insertImageFile={pad.insertImageFile} uploading={pad.uploading} rightSlot={rightSlot} />
        ) : (
          <div className="sp-toolbar">
            <Icon name="view_agenda" className="text-[18px] text-cos-ink-secondary" />
            <span className="px-1 text-[12.5px] text-cos-ink-secondary">Guided form — fill in the fields; the mail is built from them in the house layout.</span>
            <span className="sp-toolbar-grow" />
            {rightSlot}
          </div>
        )}
      </div>

      {recoverable ? (
        <div className="tpl-banner">
          <Icon name="history" className="text-[18px]" />
          <span>An unsaved draft from {fmtTime(recoverable.at)} was found in this browser.</span>
          <div className="tpl-banner-actions">
            <button type="button" className="tpl-link-btn" onClick={restoreDraft}>
              Restore it
            </button>
            <button type="button" className="tpl-link-btn !text-cos-ink-secondary" onClick={discardDraft}>
              Discard
            </button>
          </div>
        </div>
      ) : null}

      {/* ------------------------------------------------------------ canvas */}
      <div className="tpl-canvas">
        <div ref={scrollRef} className="tpl-sheet-scroll" onMouseDown={(e) => { if (e.target === e.currentTarget) pad.editor?.commands.focus('end'); }}>
          {mode === 'scratch' ? (
            <>
              <ScratchSheet editor={pad.editor} envelope={<Envelope d={d} set={set} />} zoom={zoom} />
              <SelectionBubble editor={pad.editor} containerRef={scrollRef} />
            </>
          ) : (
            <div className="sp-canvas-inner" style={{ zoom, width: 760 }}>
              <div className="sp-paper">
                <Envelope d={d} set={set} />
                <div className="sp-paper-form">
                  <BlocksForm d={d} set={set} ctaHalf={ctaHalf} />
                </div>
              </div>
            </div>
          )}
        </div>
        {previewOpen ? <PreviewPane preview={preview} shared={mode === 'blocks' && d.sharedAudience} /> : null}
      </div>

      {/* ------------------------------------------------------------ status */}
      <div className="tpl-status">
        <span className="inline-flex items-center gap-1.5">
          <Icon name={status[1]} className={`text-[15px] ${saving ? 'animate-spin' : ''}`} />
          {status[2]}
        </span>
        {mode === 'scratch' ? (
          <span>
            {counts.words} words · {counts.images} image{counts.images === 1 ? '' : 's'} · {counts.fields} vendor field{counts.fields === 1 ? '' : 's'}
            {counts.buttons ? ` · ${counts.buttons} button${counts.buttons === 1 ? '' : 's'}` : ''}
            {counts.tables ? ` · ${counts.tables} table${counts.tables === 1 ? '' : 's'}` : ''}
          </span>
        ) : null}
        <span className="tpl-status-grow" />
        <div className="tpl-zoom" aria-label="Sheet zoom">
          <button type="button" className="sp-tb" title="Zoom out" aria-label="Zoom out" disabled={zoomIdx === 0} onClick={() => setZoomIdx((i) => Math.max(0, i - 1))}>
            <Icon name="remove" className="text-[18px]" />
          </button>
          <button type="button" className="tpl-zoom-value" title="Reset zoom" onClick={() => setZoomIdx(2)}>
            {Math.round(zoom * 100)}%
          </button>
          <button type="button" className="sp-tb" title="Zoom in" aria-label="Zoom in" disabled={zoomIdx === SHEET_ZOOMS.length - 1} onClick={() => setZoomIdx((i) => Math.min(SHEET_ZOOMS.length - 1, i + 1))}>
            <Icon name="add" className="text-[18px]" />
          </button>
        </div>
      </div>

      {/* ------------------------------------------------------------ dialogs */}
      <Modal open={leaveAsk} onClose={() => setLeaveAsk(false)} size="md" labelledBy="tpl-leave">
        <div className="p-6">
          <ModalClose onClose={() => setLeaveAsk(false)} />
          <h2 id="tpl-leave" className="text-base font-bold text-cos-ink">Leave without saving?</h2>
          <p className="mt-1 text-sm leading-[21px] text-cos-ink-secondary">This template has unsaved changes. A copy is kept in this browser until you save or discard it.</p>
          <div className="mt-5 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setLeaveAsk(false)}>
              Keep editing
            </Button>
            <Button variant="ghost" className="text-cos-status-coral hover:bg-cos-status-coral-bg" onClick={() => { setLeaveAsk(false); onClose?.(); }}>
              Leave
            </Button>
            <Button variant="primary" icon="save" onClick={async () => { const row = await doSave(); if (row) { setLeaveAsk(false); onClose?.(); } }} disabled={saving}>
              Save and leave
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={testAsk} onClose={() => !testing && setTestAsk(false)} size="md" labelledBy="tpl-test">
        <div className="p-6">
          <ModalClose onClose={() => !testing && setTestAsk(false)} />
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-cos-status-teal-bg">
              <Icon name="send" className="text-[21px] text-cos-teal-deep" />
            </span>
            <div className="min-w-0">
              <h2 id="tpl-test" className="text-base font-bold text-cos-ink">Send a test to yourself?</h2>
              <p className="mt-1 text-sm leading-[21px] text-cos-ink-secondary">
                One mail goes to <strong className="text-cos-ink">{user?.email || 'your login address'}</strong>, filled in as a sample vendor and marked “[Test]” in the subject. No vendor receives anything.
              </p>
            </div>
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setTestAsk(false)} disabled={testing}>
              Cancel
            </Button>
            <Button variant="primary" icon="send" onClick={sendTest} disabled={testing}>
              {testing ? 'Sending…' : 'Send the test'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ----------------------------------------------------------------------------- the overlay

/** New Template / quick edit: the workspace over the whole screen. */
export function TemplateEditor({ template, onClose, onSaved }) {
  const navigate = useNavigate();
  const draft = useTemplateDraft(template);
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);
  const handleSaved = (row) => {
    onSaved?.(row);
    // A brand-new template continues on its own page, so the next save is an update.
    if (!template && row?.id) navigate(`/campaigning-tool/templates/${row.id}`);
  };
  return createPortal(
    <div className="campaign-os tpl-overlay">
      <TemplateDocument draft={draft} chrome="overlay" onClose={onClose} onSaved={handleSaved} />
    </div>,
    document.body
  );
}

export default TemplateEditor;
